// PaymentConfirmation.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import SidebarHeader from "../components/SidebarHeader";
import { encryptDataHybrid, apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { IoWarningOutline } from "react-icons/io5";
import { PaymentHandler } from "../components/paymentHandler";
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

function PaymentConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const [showPaymentSummaryModal, setShowPaymentSummaryModal] = useState(false);
  const [balance, setBalance] = useState("0");
  const [customAmount, setCustomAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const paymentDetails = location.state;
  const [locationLoading, setLocationLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const result = await apiCall("balance", {}, true);
        if (result.status === true) {
          setBalance(result.data.balance);
        } else {
          toast.error(result.message || "An error occurred. Please try again.");
        }
      } catch (error) {
        toast.error("Network error. Please try again.");
      }
    };

    fetchBalance();

    // For DTH Recharge, always set the amount from paymentDetails
    if (paymentDetails?.service === "DTH Recharge") {
      setCustomAmount(paymentDetails?.amount?.toString() || "");
    }
    // For Bill Payment with custom amount
    else if (paymentDetails?.service === "Bill Payment" && paymentDetails?.serviceSpecificData?.billAmount) {
      setCustomAmount(paymentDetails.serviceSpecificData.billAmount);
    }
  }, [paymentDetails]);

  // Location handling - same as Login
  useEffect(() => {
    getLocation();
  }, []);

  const getLocation = () => {
    setLocationLoading(true);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "GET_LOCATION_INFO"
        })
      );
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, altitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            setUserLocation({
              address: {
                city: data.address?.city || data.address?.town || data.address?.village || '',
                state: data.address?.state || '',
                country: data.address?.country || ''
              },
              latitude,
              longitude,
              altitude: altitude || null
            });
          } catch (error) {
            setUserLocation({
              address: { city: '', state: '', country: '' },
              latitude,
              longitude,
              altitude: altitude || null
            });
          }
          setLocationLoading(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      setLocationLoading(false);
    }
  };

  window.addEventListener("message", async (event) => {
    try {
      // Check if event.data is a string or object
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else {
        // If it's already an object, use it directly
        data = event.data;
      }

      if (data.type === "LOCATION_INFO") {
        setUserLocation(data);
        setLocationLoading(false);
      }
    } catch (e) {
      setLocationLoading(false);
      toast.error("Error in message listener:", e.message);
    }
  });

  if (!paymentDetails) {
    return <Navigate to="/" replace />;
  }

  const { service, operator, logo, operator_id, number, amount, consumerName } = paymentDetails;

  // Service-specific data (only use if available)
  const serviceSpecificData = paymentDetails.serviceSpecificData || {};

  console.log("received", serviceSpecificData);

  // Get plan description (if available from service-specific data)
  const planDesc = serviceSpecificData.planDesc || "";

  // Get first line of plan description
  const getFirstLine = (description) => {
    if (!description) return "";
    const firstLine = description.split('\n')[0];
    if (description.split('\n').length > 1 || firstLine.length > 50) {
      return firstLine;
    }
    return firstLine;
  };

  const needsSeeMore = (description) => {
    if (!description) return false;
    return description.split('\n').length > 1 || description.length > 60;
  };

  // Calculate payment details
  const balanceNum = parseFloat(balance) || 0;

  // Determine the amount based on service
  let amountNum = parseFloat(amount) || 0;

  // For DTH Recharge - use custom amount if set, else use passed amount
  if (service === "DTH Recharge" && customAmount) {
    amountNum = parseFloat(customAmount) || 0;
  }
  // For Bill Payment with custom amount
  else if (service === "Bill Payment" && customAmount) {
    amountNum = parseFloat(customAmount) || 0;
  }

  let walletDeduction = balanceNum >= amountNum ? amountNum : balanceNum;
  let payableAmount = balanceNum >= amountNum ? 0 : (amountNum - balanceNum);

  // Determine wallet deduction and payable amount
  if (service === "Bill Payment") {
    walletDeduction = 0;
    payableAmount = amountNum;
  }

  walletDeduction = Math.floor(walletDeduction)
  payableAmount = Math.ceil(payableAmount)


  // Get bill payment range
  const billMin = parseFloat(serviceSpecificData?.range?.min) || 0;
  const billMax = parseFloat(serviceSpecificData?.range?.max) || 0;

  // Get DTH amount range
  const dthMin = parseFloat(serviceSpecificData?.range?.min) || 0;
  const dthMax = parseFloat(serviceSpecificData?.range?.max) || 0;

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    setAmountError(""); // Clear error when user types
  };

  const validateAmount = () => {
    const numValue = parseFloat(customAmount);
    if (!customAmount || isNaN(numValue)) {
      setAmountError("Please enter a valid amount");
      return false;
    }

    // Validate based on service type
    if (service === "Bill Payment" && serviceSpecificData.acceptPartPay) {
      if (numValue < billMin) {
        setAmountError(`Minimum amount is ₹${billMin}`);
        return false;
      }
      if (numValue > billMax) {
        setAmountError(`Maximum amount is ₹${billMax}`);
        return false;
      }
    } else if (service === "DTH Recharge") {
      if (numValue < dthMin) {
        setAmountError(`Minimum amount is ₹${dthMin}`);
        return false;
      }
      if (numValue > dthMax) {
        setAmountError(`Maximum amount is ₹${dthMax}`);
        return false;
      }
    }

    return true;
  };

  const handleLocation = () => {
    // Validate amount for DTH Recharge and Bill Payment
    if ((service === "DTH Recharge" || (service === "Bill Payment" && serviceSpecificData.acceptPartPay)) && customAmount) {
      if (!validateAmount()) {
        return;
      }
    }

    // If location is already available, show payment summary directly
    if (userLocation?.latitude) {
      setShowPaymentSummaryModal(true);
      return;
    }

    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, altitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            setUserLocation({
              address: {
                city: data.address?.city || data.address?.town || data.address?.village || '',
                state: data.address?.state || '',
                country: data.address?.country || ''
              },
              latitude,
              longitude,
              altitude: altitude || null
            });
            setLocationLoading(false);
            setShowPaymentSummaryModal(true);
          } catch (error) {
            setUserLocation({
              address: { city: '', state: '', country: '' },
              latitude,
              longitude,
              altitude: altitude || null
            });
            setLocationLoading(false);
            setShowPaymentSummaryModal(true);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationLoading(false);
          toast.error("Unable to get location. Please try again.");
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      toast.error("Location service not available");
      setLocationLoading(false);
    }
  };

  const handleConfirm = async () => {
    //wallet
    if (payableAmount === 0) {
      setLoading(true);
      // Close payment summary modal if open
      setShowPaymentSummaryModal(false);

      try {
        const storedUserData = localStorage.getItem("credentials");
        const parsedData = JSON.parse(storedUserData);

        const user_id = parsedData?.user_id || '';
        const token = parsedData?.login_token || '';

        // Use custom amount for DTH and Bill Payment if set
        let finalAmount = amount;
        if (service === "DTH Recharge" && customAmount) {
          finalAmount = customAmount;
        } else if (service === "Bill Payment" && customAmount) {
          finalAmount = customAmount;
        }

        const payload = {
          consumerName,
          number,
          operator_id,
          amount: finalAmount,
          client_order_id: "WP" + Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 90 + 10),
          longitude: userLocation?.longitude || null,
          latitude: userLocation?.latitude || null,
          address: userLocation?.address || null
        };

        if (service === "Bill Payment") {
          payload.additional = serviceSpecificData.pass_data;
          payload.form = serviceSpecificData.form;
        }

        if (service === "DTH Recharge") {
          // Add DTH specific data
          payload.additional = {
            range: serviceSpecificData.range,
            dueDate: serviceSpecificData.dueDate || "A",
            balance: serviceSpecificData.balance || "0",
            form: serviceSpecificData.form || {}
          };
        }

        // 🔐 Encrypt full request
        const encrypted = encryptDataHybrid({
          appPlatform: '',
          appVersion: '',
          buildNumber: '',
          user_id,
          token,
          payload,
        });

        if (!encrypted) throw new Error("Encryption failed");

        const timestamp = Math.floor(Date.now() / 1000);

        const response = await fetch(`${process.env.REACT_APP_API_URL}recharge.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${timestamp * 784116}`,
          },
          body: JSON.stringify({
            encryptedData: encrypted.encryptedData,
            encryptedKey: encrypted.encryptedKey,
          }),
        });

        if (!response.ok) throw new Error("Server error");

        const result = await response.json();

        if (result.status) {
          setLoading(false);
          navigate("/payment-status", {
            state: {
              status: result.order_details.status,
              amount: finalAmount,
              number: number,
              order_id: result.order_details.order_id,
              operator_ref: result.order_details.operator_ref,
              cashback: result.order_details.cashback,
              message: result.message
            }
          });
        } else {
          navigate("/payment-status", {
            state: {
              status: "Failed",
              amount: finalAmount,
              number: number,
              order_id: result.order_details.order_id,
              operator_ref: result.order_details.operator_ref,
              cashback: result.order_details.cashback,
              message: result.message
            }
          });
        }
      } catch {
        setLoading(false);
        navigate("/payment-status", {
          state: {
            status: 'Processed',
            amount: amountNum,
            number: number,
            order_id: '',
            operator_ref: '',
            cashback: '',
            message: 'Unexpected error from server'
          }
        });
      }
    }
    //direct payment
    else if (payableAmount > 0) {
      setLoading(true);
      // Close payment summary modal if open
      setShowPaymentSummaryModal(false);

      try {
        // Use custom amount for DTH and Bill Payment if set
        let finalAmount = amount;
        if (service === "DTH Recharge" && customAmount) {
          finalAmount = customAmount;
        } else if (service === "Bill Payment" && customAmount) {
          finalAmount = customAmount;
        }

        const client_order_id = "AP" + Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 90 + 10);

        const utility_payload = {
          consumerName,
          number,
          operator_id,
          amount: finalAmount,
          client_order_id
        };

        if (service === "Bill Payment") {
          utility_payload.additional = serviceSpecificData.pass_data;
          utility_payload.form = serviceSpecificData.form;
        }

        if (service === "DTH Recharge") {
          // Add DTH specific data
          utility_payload.additional = {
            range: serviceSpecificData.range,
            dueDate: serviceSpecificData.dueDate || "A",
            balance: serviceSpecificData.balance || "0",
            form: serviceSpecificData.form || {}
          };
        }

        const payload = {
          amount: payableAmount,
          platform: 1,
          utility_payload,
          client_order_id,
          longitude: userLocation?.longitude || null,
          latitude: userLocation?.latitude || null,
          address: userLocation?.address || null
        }

        const result = await apiCall(
          "payment",
          payload,
          true
        );

        if (result.status === true) {
          setPaymentResponse(result);
        } else {
          toast.error(result.message || "Failed to fetch bill");
        }
      } catch {
        toast.error("Network error. Please try again.");
      } finally {
        setLoading(true);
      }
    }
    else {
      toast.error("Payment criteria do not match.");
    }
  };

  // Helper function to get color based on biller name (for dynamic billers)
  const getBillerColor = (billerName) => {
    const colors = ["#0277BD", "#01579B", "#006064", "#004D40", "#1B5E20", "#E65100", "#B71C1C", "#311B92", "#4527A0", "#F57F17", "#0D47A1", "#BF360C", "#2E7D32", "#C62828", "#283593"];
    let hash = 0;
    for (let i = 0; i < billerName.length; i++) {
      hash = billerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getBillerLogo = (billerName) => {
    // Get first two letters of each word
    const words = billerName.split(' ');
    if (words.length === 1) {
      return billerName.substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + (words[1].charAt(0) || words[0].charAt(1))).toUpperCase();
  };

  const info = {
    logo: getBillerLogo(operator),
    color: getBillerColor(operator)
  };

  // Render Right Side - Operator Card, Amount Input & Pay Button
  const renderRightSide = () => (
    <div className="space-y-4">
      {/* Operator Info Card */}
      <div
        className="rounded-2xl p-4"
        style={{
          background: COLORS.bgColor,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: `1px solid ${COLORS.borderColor}`,
        }}
      >
        <div className="flex items-center gap-3">
          {logo ? (
            <img
              src={logo}
              alt={operator}
              className="w-11 h-11 rounded-full flex-shrink-0"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
            style={{
              background: info.color,
              display: logo ? 'none' : 'flex'
            }}
          >
            {info.logo}
          </div>

          <div className="flex-1">
            <span className="text-sm block" style={{ color: COLORS.textPrimary }}>
              {operator}
            </span>
            <span className="text-sm block" style={{ color: COLORS.textPrimary }}>
              {number}
            </span>
          </div>
        </div>
      </div>

      {/* Warning Alert for operator_id 114 */}
      {operator_id === "114" && (
        <div className="p-3 rounded-lg text-xs"
          style={{
            border: `1.5px solid ${COLORS.errorColor}`,
            background: `${COLORS.errorColor}08`,
            color: COLORS.errorColor,
          }}
        >
          <div className="flex items-start gap-2">
            <IoWarningOutline size={18} className="flex-shrink-0 mt-0.5" />
            <span>
              The amount is not for Google Pay/Gpay payment transfer, it is a redeem code for Games, Subscription and others.
            </span>
          </div>
        </div>
      )}

      {/* Bill Payment Amount Input */}
      <div
        className="rounded-2xl p-4"
        style={{
          background: COLORS.bgColor,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: `1px solid ${COLORS.borderColor}`,
        }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
          Enter Amount
        </h3>

        <div className="flex flex-col space-y-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
              ₹
            </span>
            <input
              readOnly={!(service === "Bill Payment" && serviceSpecificData.acceptPartPay)}
              type="number"
              value={customAmount || amount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
              className={`w-full rounded-lg border px-8 py-3 text-lg font-semibold focus:outline-none focus:ring-2 ${amountError ? 'border-red-500 focus:ring-red-500' : ''
                }`}
              style={{
                color: COLORS.textPrimary,
                background: COLORS.bgColor,
                borderColor: amountError ? '#ef4444' : COLORS.borderColor,
              }}
              placeholder={`Enter amount`}
            />
          </div>
          {amountError ? (
            <div className="text-sm text-red-500">{amountError}</div>
          ) : (
            <div className="flex justify-between text-xs" style={{ color: COLORS.textSecondary }}>
              <span>Min: ₹{billMin}</span>
              <span>Max: ₹{billMax}</span>
            </div>
          )}
        </div>
      </div>

      {/* Pay Now Button */}
      <button
        onClick={handleLocation}
        disabled={loading || (service === "DTH Recharge" && !customAmount)}
        className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        style={{
          background: loading || (service === "DTH Recharge" && !customAmount) ? COLORS.textSecondary : COLORS.primaryColor,
          color: COLORS.textOnPrimary,
        }}
      >
        {(loading) ? (
          <>
            <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid ${COLORS.textOnPrimary}33`, borderTopColor: COLORS.textOnPrimary }} />
            <span>Processing...</span>
          </>
        ) : (
          `Pay Now ₹${amountNum.toFixed(2)}`
        )}
      </button>
    </div>
  );

  const handlePaymentComplete = (data) => {
    toast.success(`Payment successful of amount ${data.amount}`)
  }

  const handlePaymentError = (error) => {
    toast.error(error);
    setLoading(false);
  }

  return (
    <SidebarHeader>
      <div className="flex items-center gap-3 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
        >
          <FiArrowLeft className="text-xl" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          Payment Confirmation
        </h1>
      </div>
      <div className="max-w-7xl mx-auto mb-16">
        {/* Desktop Layout - Two Columns */}
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
          {/* Left Column - Service Summary */}
          <div
            className="rounded-2xl p-4"
            style={{
              background: COLORS.bgColor,
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
              border: `1px solid ${COLORS.borderColor}`,
            }}
          >
            <h3 className="text-sm font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
              Service Summary
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Service</span>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{service}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Number/ID</span>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{number}</span>
              </div>

              {consumerName && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Consumer Name</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{consumerName}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Amount</span>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{amount}</span>
              </div>

              {/* Service-specific data display */}
              {service === "Mobile Recharge" && serviceSpecificData.circle && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Circle</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.circle}</span>
                </div>
              )}

              {service === "Mobile Recharge" && serviceSpecificData.data_pack && serviceSpecificData.data_pack !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Data</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.data_pack}</span>
                </div>
              )}

              {service === "Mobile Recharge" && serviceSpecificData.validaty && serviceSpecificData.validaty !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Validity</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.validaty}</span>
                </div>
              )}

              {service === "Mobile Recharge" && planDesc && (
                <div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm" style={{ color: COLORS.textSecondary }}>Plan</span>
                  </div>
                  <div className="text-sm mt-1 leading-relaxed" style={{ color: COLORS.textPrimary }}>
                    {getFirstLine(planDesc)}
                    {needsSeeMore(planDesc) && (
                      <button
                        onClick={() => setShowDetailsSheet(true)}
                        className="font-semibold ml-1"
                        style={{ color: COLORS.primaryColor }}
                      >
                        ...see more
                      </button>
                    )}
                  </div>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billNumber && serviceSpecificData.billNumber !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Bill Number</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billNumber}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billDate && serviceSpecificData.billDate !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Bill Date</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billDate}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.dueDate && serviceSpecificData.dueDate !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Due Date</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.dueDate}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billAmount && serviceSpecificData.billAmount !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Bill Amount</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billAmount}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billConvenienceFee && serviceSpecificData.billConvenienceFee !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Convenience Fees</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billConvenienceFee}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billNetAmount && serviceSpecificData.billNetAmount !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Net Amount</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billNetAmount}</span>
                </div>
              )}

              {/* DTH Specific Details */}
              {service === "DTH Recharge" && serviceSpecificData.dueDate && serviceSpecificData.dueDate !== "A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Due Date</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.dueDate}</span>
                </div>
              )}

              {service === "DTH Recharge" && serviceSpecificData.balance && serviceSpecificData.balance !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Account Balance</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.balance}</span>
                </div>
              )}

              {service === "Bill Payment" &&
                Array.isArray(serviceSpecificData.additional) &&
                serviceSpecificData.additional.map((item, index) => (
                  <div key={index} className="flex justify-between gap-3">
                    <span
                      className="text-sm flex-shrink-0"
                      style={{ color: COLORS.textSecondary }}
                    >
                      {item.infoName}
                    </span>
                    <span
                      className="text-sm font-semibold text-right break-all max-w-[60%]"
                      style={{ color: COLORS.textPrimary }}
                    >
                      {item.infoValue}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Right Column - Operator, Amount & Pay Button */}
          <div>
            {renderRightSide()}
          </div>
        </div>

        {/* Mobile Layout - Single Column */}
        <div className="lg:hidden space-y-4">
          {/* Operator Info Card */}
          <div
            className="rounded-2xl p-4"
            style={{
              background: COLORS.bgColor,
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
              border: `1px solid ${COLORS.borderColor}`,
            }}
          >
            <div className="flex items-center gap-3">
              {logo ? (
                <img
                  src={logo}
                  alt={operator}
                  className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
                style={{
                  background: info.color,
                  display: logo ? 'none' : 'flex'
                }}
              >
                {info.logo}
              </div>

              <div className="flex-1">
                <span className="text-sm block" style={{ color: COLORS.textPrimary }}>
                  {operator}
                </span>
                <span className="text-sm block" style={{ color: COLORS.textPrimary }}>
                  {number}
                </span>
              </div>
            </div>
          </div>

          {/* Service Summary Card */}
          <div
            className="rounded-2xl p-4"
            style={{
              background: COLORS.bgColor,
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
              border: `1px solid ${COLORS.borderColor}`,
            }}
          >
            <h3 className="text-sm font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
              Service Summary
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Service</span>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{service}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Number/ID</span>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{number}</span>
              </div>

              {consumerName && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Consumer Name</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{consumerName}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-sm" style={{ color: COLORS.textSecondary }}>Amount</span>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{amount}</span>
              </div>

              {/* Service-specific data display - same as desktop */}
              {service === "Mobile Recharge" && serviceSpecificData.circle && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Circle</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.circle}</span>
                </div>
              )}

              {service === "Mobile Recharge" && serviceSpecificData.data_pack && serviceSpecificData.data_pack !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Data</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.data_pack}</span>
                </div>
              )}

              {service === "Mobile Recharge" && serviceSpecificData.validaty && serviceSpecificData.validaty !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Validity</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.validaty}</span>
                </div>
              )}

              {service === "Mobile Recharge" && planDesc && (
                <div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm" style={{ color: COLORS.textSecondary }}>Plan</span>
                  </div>
                  <div className="text-sm mt-1 leading-relaxed" style={{ color: COLORS.textPrimary }}>
                    {getFirstLine(planDesc)}
                    {needsSeeMore(planDesc) && (
                      <button
                        onClick={() => setShowDetailsSheet(true)}
                        className="font-semibold ml-1"
                        style={{ color: COLORS.primaryColor }}
                      >
                        ...see more
                      </button>
                    )}
                  </div>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billNumber && serviceSpecificData.billNumber !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Bill Number</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billNumber}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billDate && serviceSpecificData.billDate !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Bill Date</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billDate}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.dueDate && serviceSpecificData.dueDate !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Due Date</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.dueDate}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billAmount && serviceSpecificData.billAmount !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Bill Amount</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billAmount}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billConvenienceFee && serviceSpecificData.billConvenienceFee !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Convenience Fees</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billConvenienceFee}</span>
                </div>
              )}

              {service === "Bill Payment" && serviceSpecificData.billNetAmount && serviceSpecificData.billNetAmount !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Net Amount</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.billNetAmount}</span>
                </div>
              )}

              {/* DTH Specific Details */}
              {service === "DTH Recharge" && serviceSpecificData.dueDate && serviceSpecificData.dueDate !== "A" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Due Date</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.dueDate}</span>
                </div>
              )}

              {service === "DTH Recharge" && serviceSpecificData.balance && serviceSpecificData.balance !== "0" && (
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: COLORS.textSecondary }}>Account Balance</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{serviceSpecificData.balance}</span>
                </div>
              )}

              {service === "Bill Payment" &&
                Array.isArray(serviceSpecificData.additional) &&
                serviceSpecificData.additional.map((item, index) => (
                  <div key={index} className="flex justify-between gap-3">
                    <span
                      className="text-sm flex-shrink-0"
                      style={{ color: COLORS.textSecondary }}
                    >
                      {item.infoName}
                    </span>
                    <span
                      className="text-sm font-semibold text-right break-all max-w-[60%]"
                      style={{ color: COLORS.textPrimary }}
                    >
                      {item.infoValue}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Warning Alert */}
          {operator_id === "114" && (
            <div className="p-3 rounded-lg text-xs"
              style={{
                border: `1.5px solid ${COLORS.errorColor}`,
                background: `${COLORS.errorColor}08`,
                color: COLORS.errorColor,
              }}
            >
              <div className="flex items-start gap-2">
                <IoWarningOutline size={18} className="flex-shrink-0 mt-0.5" />
                <span>
                  The amount is not for Google Pay/Gpay payment transfer, it is a redeem code for Games, Subscription and others.
                </span>
              </div>
            </div>
          )}

          <div
            className="rounded-2xl p-4"
            style={{
              background: COLORS.bgColor,
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
              border: `1px solid ${COLORS.borderColor}`,
            }}
          >
            <h3 className="text-sm font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
              Enter Amount
            </h3>

            <div className="flex flex-col space-y-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
                  ₹
                </span>
                <input
                  readOnly={!(service === "Bill Payment" && serviceSpecificData.acceptPartPay)}
                  type="number"
                  value={customAmount || amount}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  className={`w-full rounded-lg border px-8 py-3 text-lg font-semibold focus:outline-none focus:ring-2 ${amountError ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                  style={{
                    color: COLORS.textPrimary,
                    background: COLORS.bgColor,
                    borderColor: amountError ? '#ef4444' : COLORS.borderColor,
                  }}
                  placeholder={`Enter amount`}
                />
              </div>
              {amountError ? (
                <div className="text-sm text-red-500">{amountError}</div>
              ) : (
                <div className="flex justify-between text-xs" style={{ color: COLORS.textSecondary }}>
                  <span>Min: ₹{billMin}</span>
                  <span>Max: ₹{billMax}</span>
                </div>
              )}
            </div>
          </div>

          {/* Pay Now Button */}
          <button
            onClick={handleLocation}
            disabled={loading || locationLoading || (service === "DTH Recharge" && !customAmount)}
            className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: loading || locationLoading || (service === "DTH Recharge" && !customAmount) ? COLORS.textSecondary : COLORS.primaryColor,
              color: COLORS.textOnPrimary,
            }}
          >
            {(loading || locationLoading) ? (
              <>
                <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid ${COLORS.textOnPrimary}33`, borderTopColor: COLORS.textOnPrimary }} />
                <span>Processing...</span>
              </>
            ) : (
              `Pay Now ₹${amountNum.toFixed(2)}`
            )}
          </button>
        </div>

        {/* Location Loading Overlay */}
        {locationLoading && (
          <>
            <div
              className="fixed inset-0 backdrop-blur-md bg-black/40 z-[1200] animate-fadeIn"
              style={{ backdropFilter: 'blur(8px)' }}
            />
            <div className="fixed inset-0 z-[1201] flex items-center justify-center">
              <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl animate-scaleIn max-w-[320px] w-full mx-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-4 animate-spin"
                    style={{ borderTopColor: COLORS.primaryColor }} />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
                    Fetching Location
                  </h3>
                  <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
                    Please allow location access
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Payment Summary Modal - Centered */}
        {showPaymentSummaryModal && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[1100] animate-fadeIn"
              onClick={() => setShowPaymentSummaryModal(false)}
            />
            <div className="fixed inset-0 z-[1101] flex items-center justify-center p-4">
              <div
                className="rounded-2xl animate-scaleIn max-w-[480px] w-full max-h-[90vh] overflow-y-auto"
                style={{ background: COLORS.bgColor }}
              >
                {/* Header */}
                <div className="sticky top-0 pt-4 pb-2 px-5 border-b" style={{ background: COLORS.bgColor, borderColor: COLORS.borderColor }}>
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold" style={{ color: COLORS.textPrimary }}>Payment Summary</h3>
                    <button
                      onClick={() => setShowPaymentSummaryModal(false)}
                      className="p-1 rounded-full active:scale-90 transition-transform"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 pb-6">
                  {/* Payment Breakdown */}
                  <div className="mb-4 p-4 rounded-xl" style={{ background: COLORS.surfaceColor }}>
                    <h4 className="text-sm font-semibold mb-3" style={{ color: COLORS.textPrimary }}>Payment Breakdown</h4>

                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: COLORS.textSecondary }}>Recharge Amount</span>
                      <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>₹{amountNum.toFixed(2)}</span>
                    </div>

                    {walletDeduction > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm" style={{ color: COLORS.textSecondary }}>Wallet Deduction</span>
                        <span className="text-sm font-semibold text-green-600">- ₹{walletDeduction.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: COLORS.textSecondary }}>Platform Fees</span>
                      <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>+ ₹0.00</span>
                    </div>

                    <div className="h-px my-3" style={{ background: COLORS.borderColor }} />

                    <div className="flex justify-between items-center">
                      <span className="text-base font-semibold" style={{ color: COLORS.textPrimary }}>Total Payable</span>
                      <span className="text-xl font-bold" style={{ color: COLORS.primaryColor }}>
                        ₹{payableAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Wallet Balance */}
                  <div className="mb-4 p-4 rounded-xl flex justify-between items-center" style={{ background: COLORS.surfaceColor }}>
                    <span className="text-sm" style={{ color: COLORS.textSecondary }}>Wallet Balance</span>
                    <span className="text-base font-semibold" style={{ color: COLORS.textPrimary }}>₹{balanceNum.toFixed(2)}</span>
                  </div>

                  {/* Location Info */}
                  {userLocation?.address?.city && (
                    <div className="mb-4 p-4 rounded-xl flex justify-between items-center" style={{ background: COLORS.surfaceColor }}>
                      <span className="text-sm" style={{ color: COLORS.textSecondary }}>📍 Location</span>
                      <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                        {userLocation.address.city}, {userLocation.address.state}
                      </span>
                    </div>
                  )}

                  {/* Confirm Button */}
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{
                      background: loading ? COLORS.textSecondary : COLORS.primaryColor,
                      color: COLORS.textOnPrimary,
                    }}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid ${COLORS.textOnPrimary}33`, borderTopColor: COLORS.textOnPrimary }} />
                        <span>Processing...</span>
                      </>
                    ) : (
                      `Confirm Payment ₹${payableAmount.toFixed(2)}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Plan Details Modal - Centered */}
        {showDetailsSheet && planDesc && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[1100] animate-fadeIn"
              onClick={() => setShowDetailsSheet(false)}
            />
            <div className="fixed inset-0 z-[1101] flex items-center justify-center p-4">
              <div
                className="rounded-2xl animate-scaleIn max-w-[480px] w-full max-h-[90vh] overflow-y-auto"
                style={{ background: COLORS.bgColor }}
              >
                {/* Header */}
                <div className="sticky top-0 pt-4 pb-2 px-5 border-b" style={{ background: COLORS.bgColor, borderColor: COLORS.borderColor }}>
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold" style={{ color: COLORS.textPrimary }}>Plan Details</h3>
                    <button
                      onClick={() => setShowDetailsSheet(false)}
                      className="p-1 rounded-full active:scale-90 transition-transform"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 pb-6">
                  {/* Amount */}
                  <div className="text-2xl font-bold mb-4" style={{ color: COLORS.textPrimary }}>
                    ₹{amountNum.toFixed(2)}
                  </div>

                  {/* Data & Validity Cards */}
                  {serviceSpecificData.data_pack && serviceSpecificData.data_pack !== "N/A" && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="p-3 rounded-xl" style={{ background: COLORS.surfaceColor }}>
                        <div className="text-xs mb-1" style={{ color: COLORS.textSecondary }}>Data</div>
                        <div className="text-base font-semibold" style={{ color: COLORS.textPrimary }}>
                          {serviceSpecificData.data_pack}
                        </div>
                      </div>
                      <div className="p-3 rounded-xl" style={{ background: COLORS.surfaceColor }}>
                        <div className="text-xs mb-1" style={{ color: COLORS.textSecondary }}>Validity</div>
                        <div className="text-base font-semibold" style={{ color: COLORS.textPrimary }}>
                          {serviceSpecificData.validaty}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Talktime if available */}
                  {serviceSpecificData.talktime && serviceSpecificData.talktime !== "N/A" && (
                    <div className="mb-4 p-3 rounded-xl" style={{ background: COLORS.surfaceColor }}>
                      <div className="text-xs mb-1" style={{ color: COLORS.textSecondary }}>Talktime</div>
                      <div className="text-base font-semibold" style={{ color: COLORS.textPrimary }}>
                        {serviceSpecificData.talktime}
                      </div>
                    </div>
                  )}

                  {/* Full Description */}
                  <div>
                    <div className="text-sm font-semibold mb-2" style={{ color: COLORS.textPrimary }}>Description</div>
                    <div className="text-sm leading-relaxed" style={{ color: COLORS.textSecondary }}>
                      {planDesc?.split('\n').map((line, idx) => (
                        <p key={idx} className="mb-1.5">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <Toaster />

        {paymentResponse && (
          <PaymentHandler
            result={paymentResponse}
            onPaymentComplete={handlePaymentComplete}
            onPaymentError={handlePaymentError}
          />
        )}

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { 
                opacity: 0;
                transform: scale(0.9);
              }
              to { 
                opacity: 1;
                transform: scale(1);
              }
            }
            .animate-spin {
              animation: spin 0.8s linear infinite;
            }
            .animate-fadeIn {
              animation: fadeIn 0.2s ease forwards;
            }
            .animate-scaleIn {
              animation: scaleIn 0.25s ease forwards;
            }
            .active\\:scale-95:active {
              transform: scale(0.95);
            }
            .active\\:scale-90:active {
              transform: scale(0.9);
            }
          `}
        </style>
      </div>
    </SidebarHeader>
  );
}

export default PaymentConfirmation;